import { ArrowIcon } from '@/assets/icons';
import ExperienceProductGrid from '@/components/ExperienceProductGrid';
import { useViewportStore } from '@/store/useViewportStore';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useMemo } from 'react';
import styled from 'styled-components';

export default function ExperienceProductCarousel({ products }: { products: ExperienceProduct[] }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 4000, stopOnInteraction: false }),
  ]);

  const isMobile = useViewportStore((store) => store.isMobile);
  const isTablet = useViewportStore((store) => store.isTablet);
  const isDesktop = useViewportStore((store) => store.isDesktop);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  const productGroupList = useMemo(() => {
    // 데스크탑일 경우 4개씩 그룹화
    // 태블릿일 경우 3개씩 그룹화
    // 모바일일 경우 2개씩 그룹화
    if (isDesktop) {
      return products.reduce((acc, product, index) => {
        const groupIndex = Math.floor(index / 4);
        if (!acc[groupIndex]) {
          acc[groupIndex] = [];
        }
        acc[groupIndex].push(product);
        return acc;
      }, [] as ExperienceProduct[][]);
    } else if (isTablet) {
      return products.reduce((acc, product, index) => {
        const groupIndex = Math.floor(index / 3);
        if (!acc[groupIndex]) {
          acc[groupIndex] = [];
        }
        acc[groupIndex].push(product);
        return acc;
      }, [] as ExperienceProduct[][]);
    } else {
      return products.reduce((acc, product, index) => {
        const groupIndex = Math.floor(index / 2);
        if (!acc[groupIndex]) {
          acc[groupIndex] = [];
        }
        acc[groupIndex].push(product);
        return acc;
      }, [] as ExperienceProduct[][]);
    }
  }, [products, isDesktop, isTablet]);

  return (
    <CarouselWrapper>
      {
        <Button onClick={scrollPrev}>
          <LeftArrowIcon width={20} height={20} />
        </Button>
      }
      <Embla ref={emblaRef}>
        <EmblaContainer>
          {productGroupList.map((group, index) => (
            <EmblaSlide key={index}>
              <ExperienceProductGrid data={group} />
            </EmblaSlide>
          ))}
        </EmblaContainer>
      </Embla>
      {
        <Button onClick={scrollNext}>
          <RightArrowIcon width={20} height={20} />
        </Button>
      }
    </CarouselWrapper>
  );
}

const CarouselWrapper = styled.div`
  position: relative;
  display: flex;
  height: 400px;
`;

const Embla = styled.div`
  overflow: hidden;
  flex: 1;
`;

const EmblaContainer = styled.div`
  display: flex;
`;

const EmblaSlide = styled.div`
  flex: 0 0 100%;
  padding: 10px;
`;

const Button = styled.button`
  background: white;
  border: none;
  font-size: 24px;
  padding: 10px;
  position: absolute;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  background-color: white;
  color: ${({ theme }) => theme.colors.primary};
  top: 30%;
  transform: translateY(-30%);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.3);

  &:first-of-type {
    left: -16px;
  }

  &:last-of-type {
    right: -16px;
  }
`;

const LeftArrowIcon = styled(ArrowIcon)`
  path {
    fill: ${({ theme }) => theme.colors.primary};
  }
  transform: rotate(180deg);
`;

const RightArrowIcon = styled(ArrowIcon)`
  path {
    fill: ${({ theme }) => theme.colors.primary};
  }
`;
